import { Injectable } from '@angular/core';
import {
  SPEAKERS_PATHS,
  SPEAKERS_PATHS_ADDITIONAL,
  SPEAKERS_PATHS_ADDITIONAL_MOB,
  SPEAKERS_PATHS_MOB,
  SPINE_KEYS,
  SPINE_PATHS,
  SPINE_PATHS_ADDITIONAL,
  SPINE_PATHS_ADDITIONAL_MOB,
  SPINE_PATHS_MOB,
  TEXTURES_KEYS,
  TEXTURES_KEYS_ADDITIONAL,
  TEXTURES_KEYS_NIGHT_LIGHT,
  TEXTURES_PATHS,
  TEXTURES_PATHS_ADDITIONAL
} from '../../constants/textures';
import { Assets } from 'pixi.js';
import { GameResoursesService } from '../game-resourses/game-resourses.service';
import { AudioService } from '../audio/audio.service';
import { soundAssetsFirstly, soundAssetsAdditional } from '../../constants/sound';
import { AssetExtService } from '../asset-ext.service';
import { AtlasAttachmentLoader, SkeletonJson } from '@esotericsoftware/spine-pixi-v8';
import { isMobile } from '../../classes/common/helpers';

export interface LoadConfig {
  textures: boolean;
  spines: boolean;
  speakers: boolean;
  sounds: boolean;
}

export interface ResourceGroup {
  name: string;
  textures?: string[];
  spines?: string[];
  speakers?: string[];
  sounds?: any[];
}

@Injectable({
  providedIn: 'root'
})
export class LoadResoursesService {
  private progressCallbacks: ((progress: number) => void)[] = [];
  private totalResources = 0;
  private loadedResources = 0;
  private isInitialLoadComplete = false;
  private registrationPromise: Promise<void>;

  loadingState: 'loading' | 'error' | 'complete' = 'loading';
  loadingProgress = 0;
  loadedSounds = 0;
  totalSounds = 0;
  errorMessage = '';
  volume = 1;

  // Группы ресурсов для поэтапной загрузки
  private readonly initialResourceGroups: ResourceGroup[] = [
    {
      name: 'main_textures',
      textures: Object.values(TEXTURES_KEYS)
    },
    {
      name: 'main_textures_night_light',
      textures: Object.values(TEXTURES_KEYS_NIGHT_LIGHT)
    },
    {
      name: 'main_spines',
      spines: Object.keys(SPINE_PATHS)
    },
    {
      name: 'main_speakers',
      speakers: Object.keys(SPEAKERS_PATHS)
    },
    {
      name: 'main_sounds',
      sounds: soundAssetsFirstly
    }
  ];

  private readonly additionalResourceGroups: ResourceGroup[] = [
    {
      name: 'additional_textures',
      textures: Object.values(TEXTURES_KEYS_ADDITIONAL)
    },
    {
      name: 'additional_spines',
      spines: Object.keys(SPINE_PATHS_ADDITIONAL)
    },
    {
      name: 'additional_speakers',
      speakers: Object.keys(SPEAKERS_PATHS_ADDITIONAL)
    },
    {
      name: 'additional_sounds',
      sounds: soundAssetsAdditional
    }
  ];

  constructor(
    private gameResources: GameResoursesService,
    private audioService: AudioService,
    private assetExt: AssetExtService
  ) {
    this.registrationPromise = this.registerAllResources();
  }

  /**
   * Регистрирует все ресурсы в системе Assets
   */
  private async registerAllResources(): Promise<void> {
    const optimizedPaths = await this.assetExt.registerPixiTextures(TEXTURES_PATHS);
    // Регистрация основных текстур
    Object.entries(optimizedPaths).forEach(([key, value]) => {
      Assets.add({ alias: key, src: value });
    });

    const optimizedAdditionalPaths = await this.assetExt.registerPixiTextures(TEXTURES_PATHS_ADDITIONAL);
    // Регистрация дополнительных текстур
    Object.entries(optimizedAdditionalPaths).forEach(([key, value]) => {
      Assets.add({ alias: key, src: value });
    });
    const mobDev = isMobile();

    // Регистрация основных spine
    Object.entries(SPINE_PATHS).forEach(([key, value]) => {
      if (mobDev && (SPINE_PATHS_MOB[key])) {
        // Если мобильное устройство и есть мобильная версия, используем её
        Assets.add({ alias: `${key}_skeleton`, src: `${SPINE_PATHS_MOB[key]}.json` });
        Assets.add({ alias: `${key}_atlas`, src: `${SPINE_PATHS_MOB[key]}.atlas` });
      } else {
        Assets.add({ alias: `${key}_skeleton`, src: `${value}.json` });
        Assets.add({ alias: `${key}_atlas`, src: `${value}.atlas` });
      }
    });

    // Регистрация дополнительных spine
    Object.entries(SPINE_PATHS_ADDITIONAL).forEach(([key, value]) => {
      if (mobDev && (SPINE_PATHS_ADDITIONAL_MOB[key])) {
        // Если мобильное устройство и есть мобильная версия, используем её
        Assets.add({ alias: `${key}_skeleton`, src: `${SPINE_PATHS_ADDITIONAL_MOB[key]}.json` });
        Assets.add({ alias: `${key}_atlas`, src: `${SPINE_PATHS_ADDITIONAL_MOB[key]}.atlas` });
      } else {
        Assets.add({ alias: `${key}_skeleton`, src: `${value}.json` });
        Assets.add({ alias: `${key}_atlas`, src: `${value}.atlas` });
      }
    });

    // Регистрация основных speakers
    Object.entries(mobDev ? SPEAKERS_PATHS_MOB : SPEAKERS_PATHS).forEach(([key, value]) => {
      Assets.add({ alias: `${key}_skeleton`, src: `${value}.json` });
      Assets.add({ alias: `${key}_atlas`, src: `${value}.atlas` });
    });

    // Регистрация дополнительных speakers
    Object.entries(mobDev ? SPEAKERS_PATHS_ADDITIONAL_MOB : SPEAKERS_PATHS_ADDITIONAL).forEach(([key, value]) => {
      Assets.add({ alias: `${key}_skeleton`, src: `${value}.json` });
      Assets.add({ alias: `${key}_atlas`, src: `${value}.atlas` });
    });
  }

  /**
   * Основной метод загрузки ресурсов с плавным прогрессом по количеству ресурсов
   */
  async loadResources(
    resourceGroups: ResourceGroup[],
    onProgress?: (progress: number) => void
  ): Promise<void> {
    // Ожидаем завершения регистрации ресурсов
    await this.registrationPromise;

    try {
      this.loadingState = 'loading';
      this.errorMessage = '';

      // Рассчитываем общее количество ресурсов для точного прогресса
      this.calculateTotalResources(resourceGroups);
      this.loadedResources = 0;

      // Подписываемся на прогресс если передан колбек
      if (onProgress) {
        this.progressCallbacks.push(onProgress);
        // Сразу отправляем 0% прогресс
        this.updateProgressCallbacks(0);
      }

      // Загружаем каждую группу последовательно с детальным прогрессом
      for (const group of resourceGroups) {
        await this.loadResourceGroupWithProgress(group);
      }


      // Гарантируем 100% прогресс
      this.updateProgressCallbacks(100);

      this.loadingState = 'complete';
      
      // Если это начальная загрузка, обновляем флаг
      if (resourceGroups === this.initialResourceGroups) {
        this.isInitialLoadComplete = true;
        this.updateVolume();
      }

      // Очищаем колбеки прогресса
      this.progressCallbacks = [];
    } catch (error) {
      this.loadingState = 'error';
      this.errorMessage = `Ошибка загрузки ресурсов: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`;
      this.progressCallbacks = [];
      throw error;
    }
  }

  /**
   * Загружает начальные (основные) ресурсы игры
   */
  async loadInitialResources(onProgress?: (progress: number) => void): Promise<void> {
    return this.loadResources(this.initialResourceGroups, onProgress);
  }

  /**
   * Загружает дополнительные ресурсы в фоновом режиме
   */
  async loadAdditionalResources(onProgress?: (progress: number) => void): Promise<void> {
    // Не блокируем основной поток, загружаем в фоне
    return Promise.resolve()
      .then(() => this.loadResources(this.additionalResourceGroups, onProgress))
      .catch(error => {
        console.warn('Ошибка при загрузке дополнительных ресурсов:', error);
        // Не выбрасываем ошибку, чтобы не ломать игру
      });
  }

  /**
   * Загружает группу ресурсов с пошаговым прогрессом
   */
  private async loadResourceGroupWithProgress(group: ResourceGroup): Promise<void> {
    // Загрузка текстур с прогрессом
    if (group.textures && group.textures.length > 0) {
      await this.loadTexturesWithProgress(group.textures);
    }

    // Загрузка spine анимаций с прогрессом
    if (group.spines && group.spines.length > 0) {
      await this.loadSpinesWithProgress(group.spines, 'spine');
    }

    // Загрузка speakers с прогрессом
    if (group.speakers && group.speakers.length > 0) {
      await this.loadSpinesWithProgress(group.speakers, 'speaker');
    }

    // Загрузка звуков с прогрессом
    if (group.sounds && group.sounds.length > 0) {
      await this.loadSoundsWithProgress(group.sounds);
    }
  }

  /**
   * Загружает текстуры по одной для плавного прогресса
   */
  private async loadTexturesWithProgress(textureKeys: string[]): Promise<void> {
    for (const textureKey of textureKeys) {
      try {
        const texture = await Assets.load(textureKey);
        this.gameResources.setSprite(textureKey, texture);
        this.incrementProgress(1);
      } catch (error) {
        console.error(`Ошибка загрузки текстуры ${textureKey}:`, error);
        // Все равно считаем как загруженный для прогресса
        this.incrementProgress(1);
      }
    }
  }

  /**
   * Загружает spine анимации по одной
   */
  private async loadSpinesWithProgress(spineKeys: string[], type: 'spine' | 'speaker'): Promise<void> {
    for (const spineKey of spineKeys) {
      try {
        const [rawSkeleton, atlas] = await Promise.all([
          Assets.load(`${spineKey}_skeleton`),
          Assets.load(`${spineKey}_atlas`)
        ]);


        // 👇 сохраняем уже ПРАВИЛЬНЫЕ данные
        this.gameResources.setSpineData(spineKey, {
          skeleton: rawSkeleton,
          atlas
        });

        this.incrementProgress(1);
      } catch (error) {
        console.error(`Ошибка загрузки ${type} ${spineKey}:`, error);
        this.incrementProgress(1);
      }
    }
  }

  /**
   * Загружает звуки с детальным прогрессом
   */
  private async loadSoundsWithProgress(soundAssets: any[]): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      let lastReportedCount = 0;
      const totalSounds = soundAssets.length;

      // Подписываемся на события загрузки звуков
      const progressSub = this.audioService.onProgress.subscribe(({ loaded, total }) => {
        // Обновляем прогресс по каждому загруженному звуку
        if (loaded > lastReportedCount) {
          const increment = loaded - lastReportedCount;
          this.incrementProgress(increment);
          lastReportedCount = loaded;
        }
      });

      const completeSub = this.audioService.onComplete.subscribe(() => {
        progressSub.unsubscribe();
        completeSub.unsubscribe();
        errorSub.unsubscribe();
        
        // Гарантируем, что все звуки учтены
        const remaining = totalSounds - lastReportedCount;
        if (remaining > 0) {
          this.incrementProgress(remaining);
        }
        resolve();
      });

      const errorSub = this.audioService.onError.subscribe(({ error, assetName }) => {
        console.warn(`Ошибка загрузки звука ${assetName}:`, error);
        // Продолжаем загрузку, считаем этот звук как загруженный
        this.incrementProgress(1);
        lastReportedCount++;
      });

      // Начинаем загрузку звуков
      this.audioService.preloadSounds(soundAssets)
        .then(() => {
          // Если complete не сработал, гарантируем завершение
          if (lastReportedCount < totalSounds) {
            const remaining = totalSounds - lastReportedCount;
            this.incrementProgress(remaining);
          }
          resolve();
        })
        .catch(error => {
          console.error('Ошибка предзагрузки звуков:', error);
          // Считаем все оставшиеся звуки как загруженные
          const remaining = totalSounds - lastReportedCount;
          if (remaining > 0) {
            this.incrementProgress(remaining);
          }
          resolve(); // Все равно разрешаем, чтобы игра могла продолжаться
        });
    });
  }

  /**
   * Рассчитывает общее количество ресурсов для точного отображения прогресса
   */
  private calculateTotalResources(resourceGroups: ResourceGroup[]): void {
    this.totalResources = 0;
    
    resourceGroups.forEach(group => {
      if (group.textures) this.totalResources += group.textures.length;
      if (group.spines) this.totalResources += group.spines.length;
      if (group.speakers) this.totalResources += group.speakers.length;
      if (group.sounds) this.totalResources += group.sounds.length; // Теперь считаем каждый звук отдельно
    });
  }

  /**
   * Увеличивает прогресс загрузки
   */
  private incrementProgress(increment: number = 1): void {
    this.loadedResources += increment;
    
    // Ограничиваем максимальное значение
    if (this.loadedResources > this.totalResources) {
      this.loadedResources = this.totalResources;
    }
    
    const progress = Math.min(100, (this.loadedResources / this.totalResources) * 100);
    this.updateProgressCallbacks(progress);
  }

  /**
   * Вызывает все колбеки прогресса
   */
  private updateProgressCallbacks(progress: number): void {
    const roundedProgress = Math.round(progress);
    this.progressCallbacks.forEach(callback => {
      callback(roundedProgress);
    });
  }

  /**
   * Обновляет громкость звука
   */
  updateVolume(): void {
    this.audioService.setMasterVolume(this.volume);
  }

  /**
   * Перезагружает ресурсы при ошибке
   */
  retryLoading(): Promise<void> {
    if (this.isInitialLoadComplete) {
      return this.loadAdditionalResources();
    } else {
      return this.loadInitialResources();
    }
  }
}